/**
 * File utility functions
 * Contains reusable file-related helper functions
 */

import { datePatterns, options } from "../constants/constant";

/**
 * Get MIME type from file extension
 * @param filename - The filename to get MIME type for
 * @returns The MIME type string
 */
export const getMimeType = (filename: string): string => {
	const extension = filename.split(".").pop()?.toLowerCase();
	const mimeTypes: Record<string, string> = {
		pdf: "application/pdf",
		png: "image/png",
		jpg: "image/jpeg",
		jpeg: "image/jpeg",
		xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		xls: "application/vnd.ms-excel",
		doc: "application/msword",
		docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		zip: "application/zip",
		txt: "text/plain",
		csv: "text/csv",
	};
	return mimeTypes[extension || ""] || "application/octet-stream";
};

/**
 * Generate a unique file ID
 * @returns A unique string identifier
 */
export const generateFileId = (): string => {
	return Math.random().toString(36).substr(2, 9);
};

/**
 * Check if a file is a zip file
 * @param file - The file to check
 * @returns True if the file is a zip file
 */
export const isZipFile = (file: File): boolean => {
	return (
		file.name.toLowerCase().endsWith(".zip") || file.type === "application/zip"
	);
};

/**
 * Check if a file is large enough to require chunked upload
 * @param file - The file to check
 * @param threshold - The size threshold in bytes (default: 10MB)
 * @returns True if the file requires chunked upload
 */
export const isLargeFile = (
	file: File,
	threshold: number = 10 * 1024 * 1024
): boolean => {
	return file.size > threshold;
};

/**
 * Format file size in human-readable format
 * @param bytes - The file size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return "0 Bytes";

	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Validate file type against allowed types
 * @param file - The file to validate
 * @param allowedTypes - Array of allowed MIME types
 * @returns True if the file type is allowed
 */
export const validateFileType = (
	file: File,
	allowedTypes: string[]
): boolean => {
	return allowedTypes.includes(file.type);
};

/**
 * Get file extension from filename
 * @param filename - The filename to extract extension from
 * @returns The file extension (without dot)
 */
export const getFileExtension = (filename: string): string => {
	return filename.split(".").pop()?.toLowerCase() || "";
};

/**
 * Sanitize filename by removing dangerous characters
 * @param filename - The filename to sanitize
 * @returns Sanitized filename
 */
export const sanitizeFilename = (filename: string): string => {
	return filename
		.replace(/[^a-zA-Z0-9.-]/g, "_") // Replace special chars with underscore
		.replace(/_{2,}/g, "_") // Replace multiple underscores with single
		.replace(/^_|_$/g, ""); // Remove leading/trailing underscores
};

export const getDateFromFileName = (
	file: {
		original_filename: string;
		stored_filename: string;
		file_url: string;
		content_type: string;
		size: number;
	},
	index: number
): {
	id: string;
	name: string;
	date: string;
} => {
	// Try to extract date from filename or use current date
	let fileDate = new Date().toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});

	for (const pattern of datePatterns) {
		const match = file.original_filename.match(pattern);
		if (match) {
			try {
				const dateStr = match[0];
				const date = new Date(dateStr);
				if (!isNaN(date.getTime())) {
					fileDate = date.toLocaleDateString("en-US", {
						month: "short",
						day: "numeric",
						year: "numeric",
					});
					break;
				}
			} catch (e) {
				// Continue to next pattern
			}
		}
	}

	return {
		id: file.stored_filename || `file_${index}`,
		name: file.original_filename,
		date: fileDate,
	};
};

export const populateFormData = (
	formData: FormData,
	folderId: number | undefined,
	projectId: string | undefined,
	reportingPeriod: string
) => {
	if (folderId) {
		formData.append("folder_id", folderId.toString());
	}
	if (projectId) {
		formData.append("project_id", projectId);
	}
	if (reportingPeriod) {
		formData.append("target_period", reportingPeriod);
	}

	formData.append(
		"judge_prompt_version",
		options.judgePromptVersion || "default"
	);
	formData.append("skip_judge", options.skipJudge?.toString() || "false");
	formData.append(
		"preserve_structure",
		options.preserveStructure?.toString() || "true"
	);
	formData.append("use_multi_agent_v2", "true");
	return formData;
};
